/**
 * Image handling plugin for the Slate editor.
 *
 * This plugin handles image-specific concerns:
 * 1. URL Management: Creating and cleaning up object URLs
 * 2. File Validation: Ensuring files meet size and type requirements
 * 3. Void Node Behavior: Marking images as void nodes
 * 4. Drag & Drop: Handling native file drops
 *
 * Note: Document structure (node ordering, ensuring single image)
 * is handled by withDocumentStructure plugin.
 */

import { Editor, Transforms, Element, NodeEntry } from 'slate';
import { validateMMSFile } from '../utils/mms-utils';
import { CustomElement } from '../utils/element-renderer';
import { removeContactCard } from './with-contact-cards';
import { hasAttachment, hasContactCard } from './with-document-structure';

// Map to track files and their corresponding object URLs
const urlMap = new Map<File, string>();

/**
 * Create an image node with the given file
 */
function createImageNode(file: File, externalUrl?: string): CustomElement {
  // If we have an external URL (e.g., from Vercel Blob), use that
  if (externalUrl) {
    return {
      type: 'image',
      url: externalUrl,
      file,
      children: [{ text: '' }]
    } as CustomElement;
  }

  // Otherwise, use local blob URL
  const existingUrl = urlMap.get(file);
  const url = existingUrl ?? URL.createObjectURL(file);

  if (!existingUrl) {
    urlMap.set(file, url);
  }

  return {
    type: 'image',
    url,
    file,
    children: [{ text: '' }]
  } as CustomElement;
}

/**
 * Validates and inserts an image into the editor.
 * Centralizes validation and insertion logic in one place.
 *
 * @param editor - The Slate editor instance
 * @param file - The file to validate and insert
 * @param setErrorMessage - Callback to set error message in UI
 * @param externalUrl - Optional external URL (e.g., from Vercel Blob)
 * @returns true if image was inserted successfully
 */
export function validateAndInsertImage(
  editor: Editor,
  file: File,
  setErrorMessage: (message: string | null) => void,
  externalUrl?: string
): boolean {
  const { isValid, errorMessage } = validateMMSFile(file);
  setErrorMessage(errorMessage);

  if (!isValid) {
    return false;
  }

  // If there's already an attachment, remove it first
  if (hasAttachment(editor)) {
    // Force normalization to ensure we have latest state
    Editor.normalize(editor, { force: true });

    // Handle contact card replacement explicitly
    if (hasContactCard(editor)) {
      const nodeEntry = Editor.nodes(editor, {
        at: [],
        match: (n) => Element.isElement(n) && n.type === 'contact-card'
      }).next();

      if (!nodeEntry.done) {
        const [node] = nodeEntry.value;
        removeContactCard(editor, node);
      }
    } else {
      // Handle image replacement
      const nodeEntry = Editor.nodes(editor, {
        at: [],
        match: (n) => Element.isElement(n) && n.type === 'image'
      }).next();

      if (!nodeEntry.done) {
        const [node] = nodeEntry.value as NodeEntry<CustomElement>;
        removeImage(editor, node);
      }
    }
  }

  const location = editor.selection ?? Editor.end(editor, []);
  Transforms.insertNodes(editor, createImageNode(file, externalUrl), { at: location });
  return true;
}

/**
 * Removes an image element from the editor.
 * Uses fresh node references and handles race conditions with document structure plugin.
 * Note: Does not cleanup URLs to support undo/redo operations.
 * URLs are cleaned up when the editor is unmounted.
 *
 * @param editor - The Slate editor instance
 * @param element - The image element to remove
 * @returns boolean indicating if removal was successful
 */
export function removeImage(editor: Editor, element: CustomElement): boolean {
  try {
    if (element.type !== 'image') {
      return false;
    }

    // Force normalization first to let document structure settle
    Editor.normalize(editor, { force: true });

    // Get fresh reference to ensure we have the latest state
    const nodeEntry = Editor.nodes(editor, {
      at: [],
      match: (n) => Element.isElement(n) && n.type === 'image' && n.url === element.url
    }).next().value;

    if (nodeEntry) {
      const [_, path] = nodeEntry;
      Transforms.removeNodes(editor, { at: path });
      return true;
    }

    return false;
  } catch (err) {
    console.debug('Image removal failed (path invalid or node missing)', err);
    return false;
  }
}

/**
 * Clean up object URLs when they're no longer needed
 */
export function cleanupImageUrls() {
  for (const url of urlMap.values()) {
    URL.revokeObjectURL(url);
  }
  urlMap.clear();
}

/**
 * Plugin to enable image handling in the Slate editor.
 * Focuses on image-specific behavior:
 * - Void node handling
 * - Drag & drop support
 * - URL management
 */
export function withImages(editor: Editor, setErrorMessage: (message: string | null) => void) {
  const { isVoid, insertData } = editor;

  editor.isVoid = (element) => {
    return element.type === 'image' ? true : isVoid(element);
  };

  editor.insertData = (data: DataTransfer) => {
    const files = Array.from(data.files);
    const text = data.getData('text/plain');

    // Handle image files
    if (files.length > 0) {
      validateAndInsertImage(editor, files[0], setErrorMessage);
      return;
    }

    // Fall back to default handling for text
    if (text) {
      insertData(data);
    }
  };

  return editor;
}
